import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo } from 'react';
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  DynamicDrawUsage,
  IcosahedronGeometry,
  InstancedMesh,
  Matrix4,
  MeshLambertMaterial,
  Quaternion,
  CircleGeometry,
  Vector3,
} from 'three';
import type { Building } from '@map/shared';
import { containsPoint, type Territory } from '@map/worldkit';
import { prefersReducedMotion } from '../styles/tokens.js';
import { buildTerritoryGeometry } from './geometry.js';

/**
 * Knowledge as rain.
 *
 * "The example of the guidance and knowledge with which Allah has sent me is
 * like abundant rain falling on the earth. Some of it was fertile soil that
 * absorbed the water and brought forth vegetation and grass in abundance…
 * and some of it was barren." (al-Bukhari)
 *
 * Each of Ilm's faculties is a country, and how much of its quiz is held (the
 * share of its questions whose latest answer was right) decides the weather
 * over it and what grows there, in eleven stages: 0% is cracked bare earth
 * under a clear sky, every step of ten points brings more cloud, more rain and
 * more growth, and 91–100% is a downpour over an orchard heavy with fruit.
 *
 * Everything is code-built and instanced, like the camels: a handful of draw
 * calls for every faculty together.
 */
export interface RainProps {
  territories: Map<string, Territory>;
  buildings: Building[];
}

/** 0% is its own stage; then one per ten points, so 100% is stage 10. */
export function stageOf(score: number): number {
  if (!(score > 0)) return 0;
  return Math.min(10, Math.ceil(Math.min(1, score) * 10 - 1e-9));
}

export const STAGE_NAMES = [
  'Barren',
  'Parched',
  'Dry scrub',
  'First shoots',
  'Drizzle',
  'Greening',
  'Rains',
  'Meadow',
  'Lush',
  'Fruiting',
  'Abundant',
] as const;

type Plant = 'dead' | 'scrub' | 'shoot' | 'grass' | 'bush' | 'flower' | 'sapling' | 'tree' | 'fruit' | 'crops';

interface Stage {
  ground: string;
  /** Share of the ground's spots with something growing (or dead) on it. */
  cover: number;
  /** What grows, weighted. */
  plants: Array<[Plant, number]>;
  cracks: number;
  clouds: number;
  /** 0 = white wisps, 1 = heavy dark rain cloud. */
  gloom: number;
  /** Rain streaks per unit of sqrt(area). */
  rain: number;
  puddles: number;
}

const STAGES: Stage[] = [
  { ground: '#c2a172', cover: 0.05, plants: [['dead', 1]], cracks: 60, clouds: 0, gloom: 0, rain: 0, puddles: 0 },
  { ground: '#b99b6c', cover: 0.1, plants: [['dead', 3], ['scrub', 1]], cracks: 40, clouds: 1, gloom: 0, rain: 0, puddles: 0 },
  { ground: '#ac9564', cover: 0.22, plants: [['scrub', 3], ['dead', 1]], cracks: 18, clouds: 2, gloom: 0.15, rain: 0, puddles: 0 },
  { ground: '#9c9160', cover: 0.32, plants: [['shoot', 3], ['scrub', 2]], cracks: 6, clouds: 3, gloom: 0.3, rain: 40, puddles: 0 },
  { ground: '#8a9159', cover: 0.45, plants: [['shoot', 2], ['grass', 3]], cracks: 0, clouds: 4, gloom: 0.4, rain: 110, puddles: 0 },
  { ground: '#768f50', cover: 0.58, plants: [['grass', 4], ['bush', 1]], cracks: 0, clouds: 5, gloom: 0.5, rain: 200, puddles: 0 },
  { ground: '#628c48', cover: 0.7, plants: [['grass', 4], ['bush', 2], ['sapling', 1]], cracks: 0, clouds: 6, gloom: 0.58, rain: 300, puddles: 0 },
  { ground: '#528842', cover: 0.8, plants: [['grass', 3], ['flower', 2], ['bush', 2], ['sapling', 1], ['tree', 1]], cracks: 0, clouds: 7, gloom: 0.66, rain: 420, puddles: 0 },
  { ground: '#44833c', cover: 0.88, plants: [['grass', 2], ['flower', 2], ['bush', 2], ['tree', 3]], cracks: 0, clouds: 8, gloom: 0.74, rain: 560, puddles: 2 },
  { ground: '#3a7d37', cover: 0.94, plants: [['grass', 1], ['flower', 2], ['bush', 2], ['tree', 3], ['fruit', 2]], cracks: 0, clouds: 9, gloom: 0.82, rain: 720, puddles: 4 },
  { ground: '#2f7733', cover: 1, plants: [['flower', 2], ['bush', 1], ['tree', 2], ['fruit', 4], ['crops', 2]], cracks: 0, clouds: 10, gloom: 0.9, rain: 900, puddles: 7 },
];

/** Land is drawn at 1.02; the tinted ground a hair above, plants on top of it. */
const SOIL = 0.0203;
const GROUND = 1.0205;
/** A full tree's height, in world units; everything else is sized from it. */
const K = 0.016;
/** How high the weather sits over the ground. */
const CLOUD_HEIGHT = 0.045;
/** Most drops over any one country, however big. */
const MAX_RAIN = 1400;

const C = {
  trunk: new Color('#6b4a2e'),
  deadWood: new Color('#8a7a66'),
  scrub: new Color('#8d8453'),
  shoot: new Color('#9fcf5a'),
  grass: new Color('#5ea83f'),
  bush: new Color('#3f8a35'),
  sapling: new Color('#6cb04a'),
  crown: new Color('#2f7a2e'),
  crownDark: new Color('#276b2a'),
  fruit: [new Color('#d9412b'), new Color('#f08a24'), new Color('#e8c23a')],
  flower: [new Color('#f4f0e8'), new Color('#e86aa0'), new Color('#f2cf45'), new Color('#9b7fe0')],
  crops: [new Color('#e07a2a'), new Color('#4d9a3a'), new Color('#c8342c')],
  crack: new Color('#6e5534'),
  puddle: new Color('#5b8fb8'),
  cloudLight: new Color('#f4f6f8'),
  cloudDark: new Color('#5f6873'),
  rain: new Color('#b8d6f2'),
};

/** A tiny seeded generator, so a country's plants never move between loads. */
export function rng(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  let s = h >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Country {
  id: string;
  territory: Territory;
  stage: Stage;
  up: Vector3;
  east: Vector3;
  north: Vector3;
  /** Angular radius that holds the whole ring. */
  reach: number;
  /** Seeds its growth, so the same country at the same stage always looks the same. */
  seed: string;
}

/** Random points inside the country, on the unit sphere. */
export function scatter(
  c: Pick<Country, 'territory' | 'up' | 'east' | 'north' | 'reach'>,
  count: number,
  rand: () => number,
): Vector3[] {
  const out: Vector3[] = [];
  const probe = new Vector3();
  for (let tries = 0; out.length < count && tries < count * 12; tries += 1) {
    const r = c.reach * Math.sqrt(rand());
    const a = rand() * Math.PI * 2;
    probe
      .copy(c.up)
      .addScaledVector(c.east, Math.cos(a) * Math.tan(r))
      .addScaledVector(c.north, Math.sin(a) * Math.tan(r))
      .normalize();
    if (containsPoint(c.territory.ring, { x: probe.x, y: probe.y, z: probe.z })) out.push(probe.clone());
  }
  return out;
}

function pick<T>(weighted: Array<[T, number]>, u: number): T {
  const total = weighted.reduce((sum, [, w]) => sum + w, 0);
  let at = u * total;
  for (const [value, w] of weighted) {
    at -= w;
    if (at <= 0) return value;
  }
  return (weighted[weighted.length - 1] as [T, number])[0];
}

export interface Part {
  at: Vector3;
  /** Rotates +y to the local up (and turns it about that up). */
  turn: Quaternion;
  scale: Vector3;
  colour: Color;
}

/** Everything a set of countries grows, split by the mesh it is drawn with. */
function grow(countries: Country[]) {
  const trunks: Part[] = [];
  const crowns: Part[] = [];
  const cones: Part[] = [];
  const balls: Part[] = [];
  const puddles: Part[] = [];
  const Y = new Vector3(0, 1, 0);

  const place = (up: Vector3, lift: number, heading: number, lean = 0, leanDir = 0) => {
    const turn = new Quaternion().setFromUnitVectors(Y, up);
    if (lean) {
      const tip = new Quaternion().setFromAxisAngle(
        new Vector3(Math.cos(leanDir), 0, Math.sin(leanDir)),
        lean,
      );
      turn.multiply(tip);
    }
    turn.multiply(new Quaternion().setFromAxisAngle(Y, heading));
    return { at: up.clone().multiplyScalar(GROUND + lift), turn };
  };
  const nudge = (up: Vector3, c: Country, dx: number, dy: number) =>
    up.clone().addScaledVector(c.east, dx).addScaledVector(c.north, dy).normalize();

  for (const c of countries) {
    const r = rng(`${c.seed}:grow`);
    const spots = Math.round(Math.min(600, Math.max(80, Math.sqrt(c.territory.area) * 2000)));
    for (const up of scatter(c, spots, r)) {
      const u = r();
      const kind = pick(c.stage.plants, r());
      const size = 0.75 + r() * 0.5;
      const h = r() * Math.PI * 2;
      if (u > c.stage.cover) continue;
      switch (kind) {
        case 'dead': {
          const { at, turn } = place(up, 0, h, 0.25 + r() * 0.35, r() * 6.28);
          trunks.push({ at, turn, scale: new Vector3(0.05 * K, 0.5 * K * size, 0.05 * K), colour: C.deadWood });
          const b = place(up, 0.25 * K * size, h, 0.9, r() * 6.28);
          trunks.push({ ...b, scale: new Vector3(0.03 * K, 0.22 * K * size, 0.03 * K), colour: C.deadWood });
          break;
        }
        case 'scrub': {
          const { at, turn } = place(up, 0.04 * K, h);
          crowns.push({ at, turn, scale: new Vector3(0.14, 0.08, 0.14).multiplyScalar(K * size), colour: C.scrub });
          break;
        }
        case 'shoot': {
          const { at, turn } = place(up, 0.06 * K, h);
          cones.push({ at, turn, scale: new Vector3(0.04, 0.14, 0.04).multiplyScalar(K * size), colour: C.shoot });
          break;
        }
        case 'grass':
        case 'flower': {
          for (let i = 0; i < 3; i += 1) {
            const spot = nudge(up, c, (r() - 0.5) * 0.12 * K, (r() - 0.5) * 0.12 * K);
            const { at, turn } = place(spot, 0.09 * K * size, h, (r() - 0.5) * 0.5, r() * 6.28);
            cones.push({ at, turn, scale: new Vector3(0.035, 0.18, 0.035).multiplyScalar(K * size), colour: C.grass });
          }
          if (kind === 'flower') {
            for (let i = 0; i < 2; i += 1) {
              const spot = nudge(up, c, (r() - 0.5) * 0.14 * K, (r() - 0.5) * 0.14 * K);
              const { at, turn } = place(spot, 0.2 * K * size, 0);
              const colour = C.flower[Math.floor(r() * C.flower.length)] as Color;
              balls.push({ at, turn, scale: new Vector3(1, 1, 1).multiplyScalar(0.035 * K), colour });
            }
          }
          break;
        }
        case 'bush': {
          const { at, turn } = place(up, 0.14 * K * size, h);
          crowns.push({ at, turn, scale: new Vector3(0.22, 0.17, 0.22).multiplyScalar(K * size), colour: C.bush });
          break;
        }
        case 'sapling': {
          const { at, turn } = place(up, 0.2 * K * size, h);
          trunks.push({ at, turn, scale: new Vector3(0.03 * K, 0.4 * K * size, 0.03 * K), colour: C.trunk });
          const top = place(up, 0.46 * K * size, h);
          crowns.push({ ...top, scale: new Vector3(0.14, 0.16, 0.14).multiplyScalar(K * size), colour: C.sapling });
          break;
        }
        case 'tree':
        case 'fruit': {
          const { at, turn } = place(up, 0.25 * K * size, h);
          trunks.push({ at, turn, scale: new Vector3(0.06 * K, 0.5 * K * size, 0.06 * K), colour: C.trunk });
          const crownR = 0.3 * K * size;
          const top = place(up, 0.62 * K * size, h);
          crowns.push({
            ...top,
            scale: new Vector3(crownR, crownR * 1.1, crownR),
            colour: r() < 0.5 ? C.crown : C.crownDark,
          });
          if (kind === 'fruit') {
            const colour = C.fruit[Math.floor(r() * C.fruit.length)] as Color;
            for (let i = 0; i < 6; i += 1) {
              const a = r() * Math.PI * 2;
              const spot = nudge(up, c, Math.cos(a) * crownR * 0.9, Math.sin(a) * crownR * 0.9);
              const f = place(spot, 0.62 * K * size + (r() - 0.5) * crownR, 0);
              balls.push({ ...f, scale: new Vector3(1, 1, 1).multiplyScalar(0.06 * K), colour });
            }
          }
          break;
        }
        case 'crops': {
          // A short row of vegetables.
          const colour = C.crops[Math.floor(r() * C.crops.length)] as Color;
          for (let i = 0; i < 5; i += 1) {
            const step = (i - 2) * 0.09 * K;
            const spot = nudge(up, c, Math.cos(h) * step, Math.sin(h) * step);
            const { at, turn } = place(spot, 0.04 * K, 0);
            balls.push({ at, turn, scale: new Vector3(0.06, 0.05, 0.06).multiplyScalar(K), colour });
            const leaf = place(spot, 0.1 * K, r() * 6.28);
            cones.push({ ...leaf, scale: new Vector3(0.03, 0.1, 0.03).multiplyScalar(K), colour: C.grass });
          }
          break;
        }
      }
    }
    for (const up of scatter(c, c.stage.puddles, r)) {
      const { at, turn } = place(up, -0.0001, 0);
      const s = (0.25 + r() * 0.3) * K;
      puddles.push({ at, turn, scale: new Vector3(s, s * (0.6 + r() * 0.4), s), colour: C.puddle });
    }
  }
  return { trunks, crowns, cones, balls, puddles };
}

export function useInstanced(parts: Part[], make: () => BufferGeometry, material: MeshLambertMaterial) {
  const mesh = useMemo(() => {
    const m = new InstancedMesh(make(), material, Math.max(1, parts.length));
    m.count = parts.length;
    const matrix = new Matrix4();
    parts.forEach((p, i) => {
      m.setMatrixAt(i, matrix.compose(p.at, p.turn, p.scale));
      m.setColorAt(i, p.colour);
    });
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
    m.frustumCulled = false;
    m.raycast = () => {};
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, material]);
  useEffect(() => () => mesh.geometry.dispose(), [mesh]);
  return mesh;
}

export function Rain({ territories, buildings }: RainProps) {
  const countries = useMemo(() => {
    const out: Country[] = [];
    const POLE = new Vector3(0, 1, 0);
    for (const b of buildings) {
      if (b.kind !== 'faculty' || !b.faculty) continue;
      const territory = territories.get(b.regionId);
      if (!territory) continue;
      const up = new Vector3(territory.site.x, territory.site.y, territory.site.z).normalize();
      const east = new Vector3().crossVectors(Math.abs(up.y) > 0.9 ? new Vector3(1, 0, 0) : POLE, up).normalize();
      const north = new Vector3().crossVectors(up, east);
      let reach = 0;
      for (const v of territory.ring) reach = Math.max(reach, up.angleTo(new Vector3(v.x, v.y, v.z)));
      const index = stageOf(b.faculty.score);
      out.push({ id: b.id, territory, stage: STAGES[index] as Stage, up, east, north, reach, seed: `${b.id}:${index}` });
    }
    return out;
  }, [territories, buildings]);

  const growth = useMemo(() => grow(countries), [countries]);
  const material = useMemo(() => new MeshLambertMaterial({ vertexColors: false }), []);
  const flat = useMemo(() => new MeshLambertMaterial({ side: DoubleSide }), []);
  useEffect(
    () => () => {
      material.dispose();
      flat.dispose();
    },
    [material, flat],
  );

  const trunks = useInstanced(growth.trunks, () => new CylinderGeometry(1, 1.3, 1, 5), material);
  const crowns = useInstanced(growth.crowns, () => new IcosahedronGeometry(1, 0), material);
  const cones = useInstanced(growth.cones, () => new ConeGeometry(1, 1, 4), material);
  const balls = useInstanced(growth.balls, () => new IcosahedronGeometry(1, 1), material);
  const puddles = useInstanced(
    growth.puddles,
    () => new CircleGeometry(1, 12).rotateX(-Math.PI / 2),
    flat,
  );

  if (countries.length === 0) return null;
  return (
    <group>
      {countries.map((c) => (
        <Soil key={c.id} country={c} />
      ))}
      <primitive object={trunks} />
      <primitive object={crowns} />
      <primitive object={cones} />
      <primitive object={balls} />
      <primitive object={puddles} />
      <Clouds countries={countries} />
      <Showers countries={countries} />
    </group>
  );
}

/** The ground tinted to its stage, with cracks while it is dry. */
function Soil({ country }: { country: Country }) {
  const geometry = useMemo(
    () => buildTerritoryGeometry(country.territory.ring, { elevation: SOIL }),
    [country.territory],
  );
  const cracks = useMemo(() => {
    if (country.stage.cracks === 0) return null;
    const n = Math.round(country.stage.cracks * Math.min(4, Math.max(1, Math.sqrt(country.territory.area) * 12)));
    const r = rng(`${country.id}:cracks`);
    const points: number[] = [];
    const lift = 1 + SOIL + 0.0003;
    for (const start of scatter(country, n, r)) {
      // A crack is a short zigzag with a fork or two.
      let at = start.clone();
      let dir = r() * Math.PI * 2;
      for (let s = 0; s < 4; s += 1) {
        dir += (r() - 0.5) * 1.4;
        const len = (0.3 + r() * 0.5) * K;
        const next = at
          .clone()
          .addScaledVector(country.east, Math.cos(dir) * len)
          .addScaledVector(country.north, Math.sin(dir) * len)
          .normalize();
        // Cracks stay on the land rather than running out to sea.
        if (!containsPoint(country.territory.ring, { x: next.x, y: next.y, z: next.z })) break;
        points.push(at.x * lift, at.y * lift, at.z * lift, next.x * lift, next.y * lift, next.z * lift);
        at = next;
      }
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(points), 3));
    return g;
  }, [country]);
  useEffect(
    () => () => {
      geometry?.dispose();
      cracks?.dispose();
    },
    [geometry, cracks],
  );
  const colour = useMemo(() => new Color(country.stage.ground), [country.stage]);
  if (!geometry) return null;
  return (
    <group>
      <mesh geometry={geometry} raycast={() => null}>
        <meshStandardMaterial flatShading color={colour} roughness={0.95} metalness={0} />
      </mesh>
      {cracks && (
        <lineSegments geometry={cracks} raycast={() => null}>
          <lineBasicMaterial color={C.crack} transparent opacity={0.8} />
        </lineSegments>
      )}
    </group>
  );
}

/** Puffs of cloud over each country, whiter and fewer as it dries out. */
function Clouds({ countries }: { countries: Country[] }) {
  const parts = useMemo(() => {
    const out: Part[] = [];
    const Y = new Vector3(0, 1, 0);
    for (const c of countries) {
      const r = rng(`${c.id}:clouds`);
      const colour = C.cloudLight.clone().lerp(C.cloudDark, c.stage.gloom);
      for (const up of scatter(c, c.stage.clouds, r)) {
        const puffs = 3 + Math.floor(r() * 3);
        const size = K * (0.6 + c.stage.gloom * 0.6) * (0.8 + r() * 0.4);
        for (let i = 0; i < puffs; i += 1) {
          const spot = up
            .clone()
            .addScaledVector(c.east, (i - puffs / 2) * size * 0.8)
            .addScaledVector(c.north, (r() - 0.5) * size * 0.8)
            .normalize();
          const s = size * (0.7 + r() * 0.5);
          out.push({
            at: spot.clone().multiplyScalar(GROUND + CLOUD_HEIGHT + (r() - 0.5) * size * 0.3),
            turn: new Quaternion().setFromUnitVectors(Y, spot),
            scale: new Vector3(s, s * 0.55, s),
            colour,
          });
        }
      }
    }
    return out;
  }, [countries]);
  const material = useMemo(() => new MeshLambertMaterial({ transparent: true, opacity: 0.82, depthWrite: false }), []);
  useEffect(() => () => material.dispose(), [material]);
  const mesh = useInstanced(parts, () => new IcosahedronGeometry(1, 1), material);
  // Down among the trees the clouds would sit between you and the ground like
  // boulders, so they thin out as you come in under them; the rain stays.
  useFrame(({ camera }) => {
    const altitude = camera.position.length() - GROUND;
    const fade = Math.min(1, Math.max(0, (altitude - 0.15) / 0.3));
    material.opacity = 0.82 * fade;
    mesh.visible = fade > 0.01;
  });
  if (parts.length === 0) return null;
  return <primitive object={mesh} />;
}

/** Falling streaks from cloud to ground, heavier and faster the more is known. */
function Showers({ countries }: { countries: Country[] }) {
  const drops = useMemo(() => {
    const ups: Vector3[] = [];
    const phase: number[] = [];
    const speed: number[] = [];
    for (const c of countries) {
      const n = Math.min(MAX_RAIN, Math.round(c.stage.rain * Math.sqrt(c.territory.area) * 5));
      if (n === 0) continue;
      const r = rng(`${c.id}:rain`);
      for (const up of scatter(c, n, r)) {
        ups.push(up);
        phase.push(r());
        speed.push(0.9 + c.stage.gloom * 1.2 + r() * 0.3);
      }
    }
    return { ups, phase, speed };
  }, [countries]);

  const geometry = useMemo(() => {
    const g = new BufferGeometry();
    const position = new BufferAttribute(new Float32Array(drops.ups.length * 6), 3);
    position.setUsage(DynamicDrawUsage);
    g.setAttribute('position', position);
    return g;
  }, [drops]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  const still = prefersReducedMotion();
  const streak = K * 0.35;

  useFrame((state) => {
    const attr = geometry.getAttribute('position') as BufferAttribute;
    const arr = attr.array as Float32Array;
    const t = still ? 0 : state.clock.elapsedTime;
    drops.ups.forEach((up, i) => {
      const fall = ((drops.phase[i] as number) + t * (drops.speed[i] as number)) % 1;
      const top = GROUND + CLOUD_HEIGHT * (1 - fall);
      const bottom = Math.max(GROUND, top - streak);
      arr[i * 6] = up.x * top;
      arr[i * 6 + 1] = up.y * top;
      arr[i * 6 + 2] = up.z * top;
      arr[i * 6 + 3] = up.x * bottom;
      arr[i * 6 + 4] = up.y * bottom;
      arr[i * 6 + 5] = up.z * bottom;
    });
    attr.needsUpdate = true;
  });

  if (drops.ups.length === 0) return null;
  return (
    <lineSegments geometry={geometry} frustumCulled={false} raycast={() => null}>
      <lineBasicMaterial color={C.rain} transparent opacity={0.55} depthWrite={false} />
    </lineSegments>
  );
}
